import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useWalkthroughSteps } from '../lib/useWalkthroughSteps';
import { useDoc } from '../lib/useFirestore';
import { useAuth } from '../auth/AuthProvider';
import { defaultHelpContent, mergeHelpContent } from '../lib/helpContent';
import { workflowEnabledFor } from '../lib/workflowAccess';
import { submitButtonLabel } from '../lib/matterRecordPdf';
import RichText from '../components/RichText';

/**
 * The written guide, and where the walkthrough lives once somebody has been shown it.
 *
 * IN WRITING AS WELL AS IN A TOUR because they answer different questions. A walkthrough answers
 * "what is this and how does it go" — once, for somebody new. A page answers "what does
 * usually-provided mean" and "where has my PDF gone" — repeatedly, for somebody in the middle of a
 * job, who wants to search the page and leave.
 *
 * NONE OF THE TEXT LIVES HERE. It comes from lib/helpContent.js: defaults computed from the
 * settings that decide the behaviour, with an admin's own wording from `settings/helpPage` laid
 * over the top. The first version had the prose written into this component and explained approval
 * chains to a firm whose workflow is switched off. The page list is likewise generated from the
 * live form layout, for the same reason.
 */

function Section({ title, children }) {
  return (
    <section className="rounded-lg bg-white p-5 shadow dark:bg-slate-900">
      <h2 className="text-base font-semibold text-ink dark:text-slate-100">{title}</h2>
      <div className="mt-2 space-y-3 text-sm leading-relaxed text-slate-700 dark:text-slate-200">
        {children}
      </div>
    </section>
  );
}

export default function Help() {
  const { guide, loading } = useWalkthroughSteps();

  const { user } = useAuth();
  const { data: general } = useDoc('settings/general');
  const { data: stored } = useDoc('settings/helpPage');

  // The defaults follow the same two settings the intake form itself follows, so the page is right
  // about this firm before anybody edits a word of it.
  const content = useMemo(() => {
    const workflowEnabled = workflowEnabledFor(general, user?.uid);
    return mergeHelpContent(
      stored,
      defaultHelpContent({
        workflowEnabled,
        submitLabel: submitButtonLabel(general, { workflowEnabled }),
      })
    );
  }, [stored, general, user]);

  return (
    <div className="mx-auto max-w-3xl space-y-4 pb-12">
      <div className="rounded-lg bg-white p-5 shadow dark:bg-slate-900">
        <h1 className="text-xl font-semibold text-ink dark:text-slate-100">{content.title}</h1>
        <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">{content.intro}</p>
        <Link
          to="/matters/practice"
          className="mt-4 inline-block rounded bg-firm-700 px-4 py-2 text-sm font-medium text-white hover:bg-firm-900 dark:bg-firm-500 dark:hover:bg-firm-400"
        >
          Take the practice run
        </Link>
        <span className="ml-3 text-xs text-slate-500 dark:text-slate-400">
          Opens the real form with a guide beside it. Nothing you type in it is saved.
        </span>
      </div>

      {content.sections.map((section) => (
        <Section key={section.id} title={section.title}>
          {(section.body || []).map((paragraph, i) => (
            <p key={i}>
              <RichText>{paragraph}</RichText>
            </p>
          ))}
        </Section>
      ))}

      {content.showPageGuide && (
        <Section title="What each page is for">
          {loading ? (
            <p className="text-slate-500 dark:text-slate-400">Loading your firm’s form…</p>
          ) : (
            <dl className="space-y-2">
              {guide.map((page) => (
                <div key={page.id} className="flex flex-col gap-0.5 sm:flex-row sm:gap-4">
                  <dt className="font-medium text-ink sm:w-56 sm:shrink-0 dark:text-slate-100">
                    {page.title}
                  </dt>
                  <dd className="text-slate-600 dark:text-slate-300">
                    {page.description}
                    {page.conditional && (
                      <span className="text-slate-500 dark:text-slate-400">
                        {' '}
                        Only appears for some matters.
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>
          )}
          <p className="text-xs text-slate-500 dark:text-slate-400">
            This is your firm’s form as it stands today. Pages can be renamed, hidden and added, and
            this list follows them.
          </p>
        </Section>
      )}

      {content.questions.length > 0 && (
        <Section title="Questions people actually ask">
          <div className="space-y-3">
            {content.questions.map((question) => (
              <div
                key={question.id}
                className="border-t border-slate-200 pt-3 first:border-t-0 first:pt-0 dark:border-slate-700"
              >
                <p className="font-medium text-ink dark:text-slate-100">{question.q}</p>
                <div className="mt-1 space-y-2">
                  {(question.a || []).map((paragraph, i) => (
                    <p key={i}>
                      <RichText>{paragraph}</RichText>
                    </p>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}
    </div>
  );
}
